import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { collection, query, getDocs, where, deleteDoc, doc } from 'firebase/firestore';
import { saveAs } from 'file-saver';
import { Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { FilterListOff as FilterListOffIcon } from '@mui/icons-material';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Invoice } from '../types/invoice';
import {
  Container,
  Paper,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  IconButton,
  Checkbox,
  Button,
  Typography,
  CircularProgress,
  Stack,
  Alert,
  Snackbar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { DataGrid, GridColDef, GridRenderCellParams, GridValueFormatterParams, GridRowParams } from '@mui/x-data-grid';
import { Download as DownloadIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import UserInfo from '../components/UserInfo';

const Logo = styled('img')({
  height: '40px',
  marginRight: '1rem',
});

interface SearchTerms {
  empresaEmisora: string;
  empresaReceptora: string;
  importeMin: string;
  importeMax: string;
  fechaInicio: Dayjs | null;
  fechaFin: Dayjs | null;
}

interface ExportField {
  key: keyof Invoice;
  label: string;
  checked: boolean;
}

interface DeleteConfirmDialog {
  open: boolean;
  invoiceId: string | null;
}

const WEBHOOK_URL = import.meta.env.VITE_DELETE_WEBHOOK_URL || 'https://n8n.mattic.es/webhook-test/3310a113-0761-4b90-bfc5-6da1ae1d271b';
const JWT_TOKEN = import.meta.env.VITE_JWT_TOKEN;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const retryOperation = async <T,>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt);
        await sleep(delay);
      }
    }
  }
  
  throw lastError;
};

export default function Dashboard() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerms, setSearchTerms] = useState<SearchTerms>({
    empresaEmisora: '',
    empresaReceptora: '',
    importeMin: '',
    importeMax: '',
    fechaInicio: null,
    fechaFin: null
  });
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx'>('csv');
  const [exportFields, setExportFields] = useState<ExportField[]>(() => {
    const savedFields = localStorage.getItem('exportFields');
    if (savedFields) {
      return JSON.parse(savedFields);
    }
    return [
      { key: 'Fecha', label: 'Fecha', checked: true },
      { key: 'Número de Factura', label: 'Numero de factura', checked: true },
      { key: 'NIF Empresa Emisora', label: 'NIF Emisor', checked: true },
      { key: 'Nombre Empresa Emisora', label: 'Empresa Emisora', checked: true },
      { key: 'Dirección Empresa Emisora', label: 'Dirección Emisor', checked: true },
      { key: 'NIF Empresa Receptora', label: 'NiF Receptor', checked: true },
      { key: 'Nombre Empresa Receptora', label: 'Empresa Receptora', checked: true },
      { key: 'Dirección Empresa Receptora', label: 'Dirección Receptor', checked: true },
      { key: 'Fecha de Vencimiento', label: 'Fecha de Vencimiento', checked: true },
      { key: 'Método de Pago', label: 'Método de pago', checked: true },
      { key: 'Detalles de Pago', label: 'Detalles del pago', checked: true },
      { key: 'Importe Total Antes de Impuestos', label: 'Base imponible', checked: true },
      { key: 'Importe Total de Impuestos', label: 'Total IVA', checked: true },
      { key: 'Cuota Retención', label: 'Retención', checked: true },
      { key: 'Importe Total de Impuestos', label: 'Total Impuestos', checked: true },
      { key: 'Total a Pagar', label: 'Total', checked: true },
    ];
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<DeleteConfirmDialog>({
    open: false,
    invoiceId: null
  });
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();

  const fetchInvoices = async () => {
    if (!currentUser) return;
    setLoading(true);
    setInvoices([]); // Clear existing invoices before fetching
    
    const allowedUserIds = (import.meta.env.VITE_ALLOWED_USER_IDS || '')
      .split(',')
      .map((id: string) => id.trim())
      .filter((id: string) => id !== '');

    // If no allowed users are specified, only show current user's invoices
    const userIds = allowedUserIds.length > 0 ? allowedUserIds : [currentUser.uid];

    try {
      const q = query(
        collection(db, 'invoices'),
        where('userId', 'in', userIds)
      );
      const querySnapshot = await getDocs(q);
      const invoicesData = querySnapshot.docs.map(doc => {
        const rawData = doc.data();
        // Ensure we're not duplicating data by checking the structure
        const data = rawData.data && typeof rawData.data === 'object' ? rawData.data : rawData;
        let createdAt = null;
        
        // Handle different date formats
        if (rawData.createdAt) {
          createdAt = rawData.createdAt.toDate();
        } else if (rawData.date) {
          createdAt = rawData.date.toDate();
        }
        
        return {
          id: doc.id,
          ...data,
          createdAt,
          userId: rawData.userId
        } as Invoice;
      });
      
      // Filter out any undefined or invalid entries
      const validInvoices = invoicesData.filter(invoice => 
        invoice && 
        invoice.id && 
        invoice['Nombre Empresa Emisora'] && 
        invoice['Total a Pagar']
      );
      
      // Remove duplicates based on invoice ID
      const uniqueInvoices = validInvoices.filter((invoice, index, self) =>
        index === self.findIndex((i) => i.id === invoice.id)
      );
      
      setInvoices(uniqueInvoices);
      setError(null);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError('Error al cargar las facturas. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [currentUser]);

  const handleDeleteInvoice = async () => {
    if (!deleteConfirmDialog.invoiceId) return;
    
    const invoice = invoices.find(inv => inv.id === deleteConfirmDialog.invoiceId);
    let deleteError = null;
    
    try {
      // Delete from Firebase first
      await retryOperation(async () => {
        try {
          await deleteDoc(doc(db, 'invoices', deleteConfirmDialog.invoiceId!));
        } catch (error) {
          console.error('Error deleting from Firebase:', error);
          throw new Error('Error al eliminar la factura de la base de datos');
        }
      });
      
      // Delete file via webhook if file_id exists
      if (invoice?.file_id) {
        await retryOperation(async () => {
          try {
            const response = await fetch(WEBHOOK_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(JWT_TOKEN && { 'Authorization': `Bearer ${JWT_TOKEN}` }),
              },
              body: JSON.stringify({
                fileId: invoice.file_id
              })
            });

            if (!response.ok) {
              throw new Error(`Webhook responded with status: ${response.status}`);
            }
          } catch (error) {
            if (error instanceof TypeError && error.message.includes('fetch')) {
              console.error('Network error calling webhook:', error);
              throw new Error('Error de conexión al eliminar el archivo');
            }
            console.error('Error calling webhook:', error);
            throw error;
          }
        });
      }
      
      // Refresh the invoice list
      try {
        await fetchInvoices();
      } catch (error) {
        console.error('Error refreshing invoices after deletion:', error);
        deleteError = 'La factura se eliminó pero hubo un error al actualizar la lista. Por favor, actualice la página.';
      }
      
      setDeleteConfirmDialog({ open: false, invoiceId: null });
      if (deleteError) {
        setError(deleteError);
      } else {
        setError(null);
      }
    } catch (error) {
      console.error('Error in handleDeleteInvoice:', error);
      let errorMessage = 'Error al eliminar la factura. ';
      
      if (error instanceof Error) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Por favor, inténtelo de nuevo.';
      }
      
      setError(errorMessage);
      setDeleteConfirmDialog({ open: false, invoiceId: null });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'Fecha',
      headerName: 'Fecha',
      flex: 1,
      minWidth: 100,
      sortComparator: (v1: string, v2: string) => {
        const date1 = dayjs(v1, 'DD.MM.YYYY');
        const date2 = dayjs(v2, 'DD.MM.YYYY');
        return date1.valueOf() - date2.valueOf();
      }
    },
    {
      field: 'Nombre Empresa Emisora',
      headerName: 'Empresa Emisora',
      flex: 1.5,
      sortable: true,
      minWidth: 150
    },
    {
      field: 'Nombre Empresa Receptora',
      headerName: 'Empresa Receptora',
      flex: 2,
      sortable: true,
      minWidth: 150
    },
    {
      field: 'createdAt',
      headerName: 'Fecha de Creación',
      flex: 1,
      sortable: true,
      minWidth: 120,
      valueFormatter: (params: GridValueFormatterParams) => {
        if (!params.value) return 'No disponible';
        try {
          return new Date(params.value).toLocaleString('es-ES');
        } catch (e) {
          return 'No disponible';
        }
      }
    },
    {
      field: 'Total a Pagar',
      headerName: 'Total a Pagar',
      flex: 1.5,
      sortable: true,
      minWidth: 120,
      valueFormatter: (params: GridValueFormatterParams) => {
        return `${params.value} €`;
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 0.8,
      sortable: false,
      minWidth: 80,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.userId === currentUser?.uid) {
          return (
            <IconButton
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setDeleteConfirmDialog({
                  open: true,
                  invoiceId: params.row.id
                });
              }}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          );
        }
        return null;
      }
    }
  ];

  const filteredInvoices = invoices.filter((invoice: Invoice) => {
    // Normalize search terms
    const searchTermEmisor = searchTerms.empresaEmisora.toLowerCase().trim().split(/\s+/);
    const searchTermReceptor = searchTerms.empresaReceptora.toLowerCase().trim().split(/\s+/);
    
    // Check if all search terms match for empresa emisora
    const emisorMatch = searchTermEmisor.length === 0 || searchTermEmisor.every(term => {
      const nombreEmisor = String(invoice['Nombre Empresa Emisora'] || '').toLowerCase();
      const nifEmisor = String(invoice['NIF Empresa Emisora'] || '').toLowerCase();
      return nombreEmisor.includes(term) || nifEmisor.includes(term);
    });
    
    // Check if all search terms match for empresa receptora
    const receptorMatch = searchTermReceptor.length === 0 || searchTermReceptor.every(term => {
      const nombreReceptor = String(invoice['Nombre Empresa Receptora'] || '').toLowerCase();
      const nifReceptor = String(invoice['NIF Empresa Receptora'] || '').toLowerCase();
      return nombreReceptor.includes(term) || nifReceptor.includes(term);
    });
    
    // Parse invoice amount (handling comma as decimal separator)
    const amount = parseFloat(String(invoice['Total a Pagar']).replace(/[^0-9,]+/g, '').replace(',', '.'));
    
    // Parse min/max amounts (handling comma as decimal separator)
    const minAmount = searchTerms.importeMin ? parseFloat(searchTerms.importeMin.replace(',', '.')) : null;
    const maxAmount = searchTerms.importeMax ? parseFloat(searchTerms.importeMax.replace(',', '.')) : null;
    
    const importeMinMatch = !minAmount || amount >= minAmount;
    const importeMaxMatch = !maxAmount || amount <= maxAmount;
    
    // Check fecha range
    const invoiceDate = dayjs(invoice.Fecha, 'DD.MM.YYYY');
    const fechaInicioMatch = !searchTerms.fechaInicio || 
      invoiceDate.isAfter(searchTerms.fechaInicio, 'day') || 
      invoiceDate.isSame(searchTerms.fechaInicio, 'day');
    const fechaFinMatch = !searchTerms.fechaFin || 
      invoiceDate.isBefore(searchTerms.fechaFin, 'day') || 
      invoiceDate.isSame(searchTerms.fechaFin, 'day');
    
    // All conditions must match
    return emisorMatch && receptorMatch && importeMinMatch && importeMaxMatch && 
           fechaInicioMatch && fechaFinMatch;
  });

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/invoice/${params.id}`);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleExportFieldChange = (index: number) => {
    const newFields = [...exportFields];
    newFields[index].checked = !newFields[index].checked;
    setExportFields(newFields);
    localStorage.setItem('exportFields', JSON.stringify(newFields));
  };

  const handleExport = async () => {
    const selectedFields = exportFields.filter((field: ExportField) => field.checked);
    const dataToExport = filteredInvoices;

    const parseNumericValue = (value: string | undefined): number | string => {
      if (!value || value === '') return '';
      
      // Remove currency symbols, spaces, and convert comma to dot
      const cleanValue = String(value).replace(/[€$\s]/g, '').replace(',', '.');
      const numericValue = parseFloat(cleanValue);
      
      // Return number if it's valid, otherwise return original string
      return !isNaN(numericValue) ? numericValue : value;
    };

    const data = dataToExport.map((invoice: Invoice) =>
      selectedFields.reduce((acc: Record<string, any>, field: ExportField) => {
        let value: number | string = '';

        if (field.label === 'Total IVA' && Array.isArray(invoice.IVA)) {
          // Sum all IVA amounts
          const totalIVA = invoice.IVA.reduce((sum: number, iva: any) => {
            const amount = parseFloat(String(iva['Importe de IVA'] || '0').replace(/[^0-9,.-]/g, '').replace(',', '.'));
            return sum + (isNaN(amount) ? 0 : amount);
          }, 0);
          value = totalIVA;
        } else if (field.label === 'Total Impuestos') {
          value = parseNumericValue(invoice['Importe Total de Impuestos']);
        } else if (field.label === 'Retención') {
          value = parseNumericValue(invoice['Cuota Retención']);
        } else if (field.label === 'Base imponible') {
          value = parseNumericValue(invoice['Importe Total Antes de Impuestos']);
        } else if (field.label === 'Total') {
          value = parseNumericValue(invoice['Total a Pagar']);
        } else {
          // For other fields, try to parse them if they look like numbers
          const rawValue = invoice[field.key];
          if (typeof rawValue === 'string' && /^[\d\s€$,.-]+$/.test(rawValue)) {
            value = parseNumericValue(rawValue);
          } else {
            value = String(rawValue || '');
          }
        }
        
        acc[field.label] = value;
        return acc;
      }, {})
    );

    if (exportFormat === 'xlsx') {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      
      // Define numeric fields and set proper formatting
      const numericFields = ['Base imponible', 'Total IVA', 'Retención', 'Total Impuestos', 'Total'];
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      
      // Find column indices for numeric fields and format them
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const headerCell = ws[XLSX.utils.encode_cell({r: 0, c: C})];
        if (headerCell && numericFields.includes(String(headerCell.v))) {
          // Format all cells in this column as numbers
          for (let R = range.s.r + 1; R <= range.e.r; ++R) {
            const cellAddress = XLSX.utils.encode_cell({r: R, c: C});
            const cell = ws[cellAddress];
            if (cell && typeof cell.v === 'number') {
              cell.t = 'n'; // Set cell type to number
              cell.z = '#,##0.00'; // Set number format with comma thousands separator and 2 decimals
            }
          }
        }
      }
      
      XLSX.utils.book_append_sheet(wb, ws, "Facturas");
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `facturas_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    } else {
      // For CSV, format numbers with comma as decimal separator
      const csvData = data.map(row => {
        const formattedRow = { ...row };
        const numericFields = ['Base imponible', 'Total IVA', 'Retención', 'Total Impuestos', 'Total'];
        
        Object.keys(formattedRow).forEach(key => {
          if (typeof formattedRow[key] === 'number' && numericFields.includes(key)) {
            formattedRow[key] = formattedRow[key].toFixed(2).replace('.', ',');
          }
        });
        
        return formattedRow;
      });
      
      const csvContent = [
        selectedFields.map(field => field.label).join(';'),
        ...csvData.map(row => 
          selectedFields
            .map(field => `"${row[field.label] || ''}"`)
            .join(';')
        )
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `facturas_${dayjs().format('YYYY-MM-DD')}.csv`);
    }

    setOpenExportDialog(false);
  };

  const handleResetFilters = () => {
    setSearchTerms({
      empresaEmisora: '',
      empresaReceptora: '',
      importeMin: '',
      importeMax: '',
      fechaInicio: null,
      fechaFin: null
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: '100vh', p: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Logo
            src="/logo.png"
            alt="Factumattic Logo"
          />
          <Typography variant="h4" component="h1" sx={{ fontFamily: '"Prompt", sans-serif', color: '#047C84' }}>
            Repositorio de Facturas
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => setOpenExportDialog(true)}
            sx={{ mr: 2 }}
          >
            Exportar
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchInvoices()}
            sx={{ mr: 2 }}
          >
            Actualizar
          </Button>
          <Button variant="contained" color="primary" onClick={handleLogout}>
            Cerrar Sesión
          </Button>
        </Box>
      </Box>
      
      <Dialog open={openExportDialog} onClose={() => setOpenExportDialog(false)}>
        <DialogTitle>Exportar Facturas</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2, minWidth: 400 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Se exportarán {filteredInvoices.length} facturas según los filtros aplicados actualmente.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" gutterBottom>Formato de exportación</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={exportFormat === 'csv' ? 'contained' : 'outlined'}
                  onClick={() => setExportFormat('csv')}
                >
                  CSV
                </Button>
                <Button
                  variant={exportFormat === 'xlsx' ? 'contained' : 'outlined'}
                  onClick={() => setExportFormat('xlsx')}
                >
                  Excel
                </Button>
              </Box>
            </Box>
            <Typography variant="subtitle1">Campos a exportar</Typography>
            <FormGroup>
              {exportFields.map((field, index) => (
                <FormControlLabel
                  key={field.key}
                  control={
                    <Checkbox
                      checked={field.checked}
                      onChange={() => handleExportFieldChange(index)}
                    />
                  }
                  label={field.label}
                />
              ))}
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExportDialog(false)}>Cancelar</Button>
          <Button onClick={handleExport} variant="contained" color="primary">
            Descargar {exportFormat === 'csv' ? 'CSV' : 'Excel'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Paper sx={{ p: 2, mb: 3, height: 'calc(100vh - 180px)' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FilterListOffIcon />}
              onClick={handleResetFilters}
              size="small"
            >
              Resetear filtros
            </Button>
          </Box>
          <TextField
            label="Buscar por empresa/NIF emisor"
            variant="outlined"
            value={searchTerms.empresaEmisora}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerms(prev => ({ ...prev, empresaEmisora: e.target.value }))}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Buscar por empresa/NIF receptor"
            variant="outlined"
            value={searchTerms.empresaReceptora}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerms(prev => ({ ...prev, empresaReceptora: e.target.value }))}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Importe mínimo"
            variant="outlined"
            value={searchTerms.importeMin}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerms(prev => ({ ...prev, importeMin: e.target.value }))}
            sx={{ flex: 0.5 }}
            InputProps={{
              endAdornment: <Typography>€</Typography>
            }}
            inputProps={{
              inputMode: 'decimal',
              pattern: '[0-9]*[,]?[0-9]*'
            }}
          />
          <TextField
            label="Importe máximo"
            variant="outlined"
            value={searchTerms.importeMax}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerms(prev => ({ ...prev, importeMax: e.target.value }))}
            sx={{ flex: 0.5 }}
            InputProps={{
              endAdornment: <Typography>€</Typography>
            }}
            inputProps={{
              inputMode: 'decimal',
              pattern: '[0-9]*[,]?[0-9]*'
            }}
          />
          <DatePicker
            label="Fecha inicio"
            format="DD/MM/YYYY"
            value={searchTerms.fechaInicio}
            onChange={(newValue: Dayjs | null) => setSearchTerms(prev => ({ ...prev, fechaInicio: newValue }))}
            sx={{ flex: 0.5 }}
          />
          <DatePicker
            label="Fecha fin"
            format="DD/MM/YYYY"
            value={searchTerms.fechaFin}
            onChange={(newValue: Dayjs | null) => setSearchTerms(prev => ({ ...prev, fechaFin: newValue }))}
            sx={{ flex: 0.5 }}
          />
        </Box>
        
        <div style={{ height: 'calc(100% - 80px)', width: '100%' }}>
          <DataGrid
            rows={filteredInvoices}
            columns={columns}
            initialState={{
              sorting: {
                sortModel: [{ field: 'Fecha', sort: 'desc' }],
              },
              columns: {
                columnVisibilityModel: {
                  'Nombre Empresa Receptora': false,
                  'createdAt': false,
                  ...(window.innerWidth >= 600 ? {
                    'Nombre Empresa Receptora': true,
                    'createdAt': true,
                  } : {})
                },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            onRowClick={handleRowClick}
            sx={{
              '& .MuiDataGrid-cell': {
                padding: { xs: '8px 4px', sm: '16px 8px' },
              },
              '& .MuiDataGrid-columnHeader': {
                padding: { xs: '8px 4px', sm: '16px 8px' },
              },
            }}
          />
        </div>
      </Paper>
      
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, invoiceId: null })}
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar esta factura? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, invoiceId: null })}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteInvoice} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <UserInfo />
    </Container>
  );
}